/**
 * Marker inspector (W2-06; DESIGN.md §4.11, RPE `timeTags`): name, beat (with the time echo) and colour of a
 * marker, delete; several markers share the colour field and delete. Markers are named in beat fields as `m<name>`.
 */

import { useCallback, useMemo } from 'react';
import type { AppServices, InspectorSectionDef, InspectorSectionProps } from '../../../app/registry/types';
import { Core } from '../../../core/commands/index';
import type { ChartDoc, Marker, MarkerId } from '../../../core/model/types';
import { Beats } from '../../../core/time/beat';
import { useT } from '../../../i18n/useT';
import type { DocStore } from '../../../state/types';
import { BeatField, ColorField, IconButton, Section, TextField } from '../../../ui';
import { useDocValue } from '../hooks';
import { m } from '../inspector.messages';
import { arrayEq, colorFieldOf, hexToRgb, rgbToHex, sortedIds } from '../model';
import { Row, Title, useBeatCtx, useSectionBase, useStore } from '../parts';
import s from '../Inspector.module.css';
import { useEditKit } from '../tx';
import { FIELD } from '../useFieldProblems';

export function MarkerSection(props: InspectorSectionProps) {
  const store = useStore(props.services);
  const ids = sortedIds(props.selection.markers);
  if (store === null || ids.length === 0) return null;
  return <MarkerBody services={props.services} store={store} ids={ids} />;
}

function MarkerBody(props: {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly ids: readonly MarkerId[];
}) {
  const { services, store } = props;
  const t = useT();
  const base = useSectionBase(services, store);
  const beatCtx = useBeatCtx(services, base);
  const key = props.ids.join(',');
  const selector = useCallback(
    (doc: ChartDoc) =>
      (key === '' ? [] : key.split(',').map(Number))
        .map((id) => doc.markers.get(id as MarkerId))
        .filter((x): x is Marker => x !== undefined)
        .sort((a, b) => Beats.cmp(a.t, b.t)),
    [key],
  );
  const markers = useDocValue(store, selector, ['markers'], arrayEq);
  const ids = useMemo(() => markers.map((x) => x.id), [markers]);
  const edit = useEditKit(services, store, `markers:${ids.join(',')}`);
  const first = markers[0];
  if (first === undefined) return null;
  const single = markers.length === 1 ? first : null;
  const colours = markers.map((x) => (x.color === null ? null : hexToRgb(x.color)));
  const colour = colorFieldOf(colours);

  return (
    <Section
      title={
        <Title
          main={single === null ? t(m.markersTitle, { count: markers.length }) : t(m.markerTitle)}
          detail={single?.name ?? ''}
        />
      }
      actions={
        <IconButton
          icon="Trash2"
          size="sm"
          label={{ key: single === null ? m.deleteMarkers : m.deleteMarker }}
          onClick={() => {
            edit.once({ key: m.txDeleteMarkers }, (tx) => {
              for (const id of ids) Core.tempo.deleteMarker(tx, { id });
            });
          }}
        />
      }
    >
      <div className={s.fields}>
        {single !== null && (
          <>
            <Row edit={edit} field={FIELD.markerName}>
              <TextField
                label={t(m.markerName)}
                value={single.name}
                helpId={FIELD.markerName}
                onCommit={(name) => {
                  if (name === single.name) return;
                  edit.commit(FIELD.markerName, { key: m.txMarker }, (tx) => {
                    Core.tempo.patchMarker(tx, { id: single.id, name });
                  });
                }}
              />
            </Row>
            <Row edit={edit} field={FIELD.markerBeat}>
              <BeatField
                label={t(m.markerBeat)}
                value={{ kind: 'value', value: single.t }}
                ctx={beatCtx}
                echo
                helpId={FIELD.markerBeat}
                onCommit={(b) => {
                  edit.commit(FIELD.markerBeat, { key: m.txMarker }, (tx) => {
                    Core.tempo.patchMarker(tx, { id: single.id, t: b });
                  });
                }}
              />
            </Row>
          </>
        )}
        <Row edit={edit} field="marker.color">
          <ColorField
            label={t(m.markerColour)}
            value={colour}
            allowNone
            helpId="marker.color"
            onCommit={(c) => {
              const color = c === null ? null : rgbToHex(c);
              edit.commit('marker.color', { key: m.txMarker }, (tx) => {
                for (const id of ids) Core.tempo.patchMarker(tx, { id, color });
              });
            }}
          />
        </Row>
      </div>
    </Section>
  );
}

export const markerSectionDef: InspectorSectionDef = {
  id: 'marker',
  order: 450,
  level: 'guided',
  appliesTo: (sel) => sel.markers.size > 0,
  component: MarkerSection,
};
